import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const AccountsSetup = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('accounts');

  // States
  const [accounts, setAccounts] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showFiscalYearModal, setShowFiscalYearModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [filterType, setFilterType] = useState('all');

  // Color palette
  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  // Responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeTab, filterType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'accounts') {
        const url = filterType === 'all'
          ? '/admin/accounts/setup/accounts'
          : `/admin/accounts/setup/accounts?account_type=${filterType}`;
        const data = await apiGet(url);
        if (data.success) {
          setAccounts(data.data);
        }
      } else if (activeTab === 'fiscal-years') {
        const data = await apiGet('/admin/accounts/setup/fiscal-years');
        if (data.success) {
          setFiscalYears(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handleCreateAccount = async (formData) => {
    try {
      const response = await fetch('/admin/accounts/setup/accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        alert('Account created successfully!');
        setShowAccountModal(false);
        setEditingAccount(null);
        fetchData();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error creating account:', error);
      alert('Error creating account');
    }
  };

  const handleUpdateAccount = async (formData) => {
    try {
      const response = await fetch(`/admin/accounts/setup/accounts/${editingAccount.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        alert('Account updated successfully!');
        setShowAccountModal(false);
        setEditingAccount(null);
        fetchData();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating account:', error);
      alert('Error updating account');
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Are you sure you want to delete this account?')) return;

    try {
      const data = await apiDelete(`/admin/accounts/setup/accounts/${id}`);
      if (data.success) {
        alert('Account deleted successfully!');
        fetchData();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Error deleting account');
    }
  };

  const handleCreateFiscalYear = async (formData) => {
    try {
      const response = await fetch('/admin/accounts/setup/fiscal-years', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        alert('Fiscal year created successfully!');
        setShowFiscalYearModal(false);
        fetchData();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error creating fiscal year:', error);
      alert('Error creating fiscal year');
    }
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    pageSubtitle: {
      fontSize: isMobile ? '12px' : '14px',
      color: colors.textLight
    },
    tabContainer: {
      display: 'flex',
      gap: isMobile ? '8px' : '12px',
      marginBottom: isMobile ? '16px' : '20px',
      padding: isMobile ? '0 4px' : '0',
      flexWrap: 'wrap'
    },
    tab: (active) => ({
      padding: isMobile ? '10px 16px' : '12px 24px',
      background: active ? colors.primary : colors.white,
      color: active ? colors.white : colors.textDark,
      border: `2px solid ${active ? colors.primary : colors.mediumGray}`,
      borderRadius: '10px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }),
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '18px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px' : '0'
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: isMobile ? '16px' : '20px',
      flexWrap: 'wrap',
      gap: '12px'
    },
    sectionTitle: {
      fontSize: isMobile ? '18px' : '22px',
      fontWeight: '700',
      color: colors.textDark
    },
    button: {
      padding: isMobile ? '10px 16px' : '12px 20px',
      background: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: '8px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    filterContainer: {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
      flexWrap: 'wrap'
    },
    filterButton: (active) => ({
      padding: '8px 16px',
      background: active ? colors.accent : colors.lightGray,
      color: active ? colors.white : colors.textDark,
      border: 'none',
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer'
    }),
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    th: {
      background: colors.lightGray,
      padding: isMobile ? '10px' : '12px',
      textAlign: 'left',
      fontWeight: '700',
      borderBottom: `2px solid ${colors.mediumGray}`,
      fontSize: isMobile ? '11px' : '13px'
    },
    td: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    },
    badge: (type) => {
      const colorMap = {
        asset: colors.primary,
        liability: colors.danger,
        equity: colors.accent,
        revenue: colors.success,
        expense: colors.warning
      };
      return {
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: '600',
        background: `${colorMap[type] || colors.textLight}20`,
        color: colorMap[type] || colors.textLight
      };
    },
    actionButton: {
      padding: '6px 12px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      marginRight: '6px'
    }
  };

  const AccountModal = () => {
    const [formData, setFormData] = useState(editingAccount || {
      account_code: '',
      account_name: '',
      account_type: 'asset',
      parent_id: null,
      opening_balance: 0,
      description: '',
      is_active: true
    });

    const parentAccounts = accounts.filter(a => a.is_system_account);

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          background: colors.white,
          padding: isMobile ? '20px' : '30px',
          borderRadius: '16px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}>
          <h3 style={{ marginBottom: '20px', fontSize: isMobile ? '18px' : '22px' }}>
            {editingAccount ? 'Edit Account' : 'New Account'}
          </h3>

          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
                  Account Code
                </label>
                <input
                  type="text"
                  value={formData.account_code}
                  onChange={(e) => setFormData({ ...formData, account_code: e.target.value })}
                  placeholder="1001"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
                  Account Name
                </label>
                <input
                  type="text"
                  value={formData.account_name}
                  onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                  placeholder="Account name"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
                  Account Type
                </label>
                <select
                  value={formData.account_type}
                  onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
                >
                  <option value="asset">Asset</option>
                  <option value="liability">Liability</option>
                  <option value="equity">Equity</option>
                  <option value="revenue">Revenue</option>
                  <option value="expense">Expense</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
                  Parent Account
                </label>
                <select
                  value={formData.parent_id || ''}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value || null })}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
                >
                  <option value="">None</option>
                  {parentAccounts.filter(a => a.account_type === formData.account_type).map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.account_code} - {acc.account_name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
                Opening Balance
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.opening_balance}
                onChange={(e) => setFormData({ ...formData, opening_balance: e.target.value })}
                placeholder="0.00"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Account description"
                rows="3"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>

            {editingAccount && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  id="is_active"
                />
                <label htmlFor="is_active" style={{ fontSize: '13px', fontWeight: '600' }}>
                  Active
                </label>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setShowAccountModal(false);
                setEditingAccount(null);
              }}
              style={{
                padding: '10px 20px',
                background: colors.mediumGray,
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => editingAccount ? handleUpdateAccount(formData) : handleCreateAccount(formData)}
              style={styles.button}
            >
              {editingAccount ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const FiscalYearModal = () => {
    const [formData, setFormData] = useState({
      year_name: '',
      start_date: '',
      end_date: ''
    });

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          background: colors.white,
          padding: isMobile ? '20px' : '30px',
          borderRadius: '16px',
          maxWidth: '500px',
          width: '100%'
        }}>
          <h3 style={{ marginBottom: '20px', fontSize: isMobile ? '18px' : '22px' }}>New Fiscal Year</h3>

          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
                Year Name
              </label>
              <input
                type="text"
                value={formData.year_name}
                onChange={(e) => setFormData({ ...formData, year_name: e.target.value })}
                placeholder="FY 2025-26"
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
                Start Date
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>
                End Date
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.mediumGray}` }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowFiscalYearModal(false)}
              style={{
                padding: '10px 20px',
                background: colors.mediumGray,
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => handleCreateFiscalYear(formData)}
              style={styles.button}
            >
              Create
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Accounts Setup</h1>
        <p style={styles.pageSubtitle}>
          {isMobile ? 'Chart of accounts' : 'Configure chart of accounts, financial settings, and accounting parameters'}
        </p>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          style={styles.tab(activeTab === 'accounts')}
          onClick={() => setActiveTab('accounts')}
        >
          📋 Chart of Accounts
        </button>
        <button
          style={styles.tab(activeTab === 'fiscal-years')}
          onClick={() => setActiveTab('fiscal-years')}
        >
          📅 Fiscal Years
        </button>
      </div>

      {/* Content */}
      <div style={styles.contentCard}>
        <div style={styles.headerRow}>
          <h2 style={styles.sectionTitle}>
            {activeTab === 'accounts' ? '📋 Chart of Accounts' : '📅 Fiscal Years'}
          </h2>
          {activeTab === 'accounts' && (
            <button style={styles.button} onClick={() => setShowAccountModal(true)}>
              + New Account
            </button>
          )}
          {activeTab === 'fiscal-years' && (
            <button style={styles.button} onClick={() => setShowFiscalYearModal(true)}>
              + New Fiscal Year
            </button>
          )}
        </div>

        {/* Filters for Accounts */}
        {activeTab === 'accounts' && (
          <div style={styles.filterContainer}>
            <button
              style={styles.filterButton(filterType === 'all')}
              onClick={() => setFilterType('all')}
            >
              All
            </button>
            <button
              style={styles.filterButton(filterType === 'asset')}
              onClick={() => setFilterType('asset')}
            >
              Assets
            </button>
            <button
              style={styles.filterButton(filterType === 'liability')}
              onClick={() => setFilterType('liability')}
            >
              Liabilities
            </button>
            <button
              style={styles.filterButton(filterType === 'equity')}
              onClick={() => setFilterType('equity')}
            >
              Equity
            </button>
            <button
              style={styles.filterButton(filterType === 'revenue')}
              onClick={() => setFilterType('revenue')}
            >
              Revenue
            </button>
            <button
              style={styles.filterButton(filterType === 'expense')}
              onClick={() => setFilterType('expense')}
            >
              Expenses
            </button>
          </div>
        )}

        {/* Accounts Table */}
        {activeTab === 'accounts' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Account Name</th>
                  {!isMobile && <th style={styles.th}>Type</th>}
                  <th style={styles.th}>Balance</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map(account => (
                  <tr key={account.id}>
                    <td style={styles.td}><strong>{account.account_code}</strong></td>
                    <td style={styles.td}>{account.account_name}</td>
                    {!isMobile && (
                      <td style={styles.td}>
                        <span style={styles.badge(account.account_type)}>
                          {account.account_type}
                        </span>
                      </td>
                    )}
                    <td style={styles.td}>
                      <strong>₹{parseFloat(account.current_balance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: account.is_active ? `${colors.success}20` : `${colors.textLight}20`,
                        color: account.is_active ? colors.success : colors.textLight
                      }}>
                        {account.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <button
                        onClick={() => {
                          setEditingAccount(account);
                          setShowAccountModal(true);
                        }}
                        style={{
                          ...styles.actionButton,
                          background: colors.accent,
                          color: colors.white
                        }}
                      >
                        Edit
                      </button>
                      {!account.is_system_account && (
                        <button
                          onClick={() => handleDeleteAccount(account.id)}
                          style={{
                            ...styles.actionButton,
                            background: colors.danger,
                            color: colors.white
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Fiscal Years Table */}
        {activeTab === 'fiscal-years' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Year Name</th>
                  <th style={styles.th}>Start Date</th>
                  <th style={styles.th}>End Date</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {fiscalYears.map(year => (
                  <tr key={year.id}>
                    <td style={styles.td}><strong>{year.year_name}</strong></td>
                    <td style={styles.td}>{new Date(year.start_date).toLocaleDateString('en-IN')}</td>
                    <td style={styles.td}>{new Date(year.end_date).toLocaleDateString('en-IN')}</td>
                    <td style={styles.td}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: year.is_active ? `${colors.success}20` : year.is_closed ? `${colors.textLight}20` : `${colors.warning}20`,
                        color: year.is_active ? colors.success : year.is_closed ? colors.textLight : colors.warning
                      }}>
                        {year.is_active ? 'Active' : year.is_closed ? 'Closed' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAccountModal && <AccountModal />}
      {showFiscalYearModal && <FiscalYearModal />}
    </AdminLayout>
  );
};

export default AccountsSetup;
